/**
 * Parent-side query for `roster_entries` linked to the signed-in
 * parent via `parent_user_id`.
 *
 * Under-13 ghost players don't have User rows in v1 (see
 * docs/spec/22a-v1-launch-posture.md). They're tracked in
 * `roster_entries`. A parent linked to one (or more) of those
 * entries via `roster_entries.parent_user_id = auth.uid()` reads
 * them through this hook.
 *
 * RLS permits this read per migration 00084 policy
 * `roster_entries_parent_read`.
 *
 * Distinct from `useLinkedChildren`, which surfaces 13+ children
 * with User rows + `parent_children` link rows. A parent can have
 * both: some teens with accounts (useLinkedChildren), some U7-U12
 * kids without accounts (this hook).
 */
import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';
import { useAuth } from '../../../contexts/AuthContext';
import type { RosterEntry } from '@4cm/core/rosterEntry';

export interface ParentRosterEntry extends RosterEntry {
  /** Optional team name — joined from `teams.name`. Null when the
   *  team row is deleted or RLS denies the join for some reason. */
  team_name: string | null;
  /** Optional team age group — useful for "U10 squad" labelling. */
  team_age_group: string | null;
}

interface State {
  data: ParentRosterEntry[];
  loading: boolean;
  error: string | null;
}

interface DrillRow extends RosterEntry {
  teams: { name: string | null; age_group: string | null } | null;
}

export function useParentRosterEntries(version = 0): State & { refetch: () => void } {
  const { user, bypass } = useAuth();
  const [state, setState] = useState<State>({ data: [], loading: true, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!user || bypass) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      const { data, error } = await supabase
        .from('roster_entries')
        .select(
          'id, team_id, first_name, last_name, dob, position, jersey_number, parent_user_id, claimed_user_id, notes, created_by, created_at, updated_at, deleted_at, teams(name, age_group)',
        )
        .eq('parent_user_id', user.id)
        .is('deleted_at', null);
      if (cancelled) return;
      if (error) {
        setState({ data: [], loading: false, error: error.message });
        return;
      }
      const rows = (data ?? []) as unknown as DrillRow[];
      const merged: ParentRosterEntry[] = rows.map((r) => ({
        id: r.id,
        team_id: r.team_id,
        first_name: r.first_name,
        last_name: r.last_name,
        dob: r.dob,
        position: r.position,
        jersey_number: r.jersey_number,
        parent_user_id: r.parent_user_id,
        claimed_user_id: r.claimed_user_id,
        notes: r.notes,
        created_by: r.created_by,
        created_at: r.created_at,
        updated_at: r.updated_at,
        deleted_at: r.deleted_at,
        team_name: r.teams?.name ?? null,
        team_age_group: r.teams?.age_group ?? null,
      }));
      // Newest first by created_at — parents typically see the most
      // recent addition surfaced at the top.
      merged.sort((a, b) => b.created_at.localeCompare(a.created_at));
      setState({ data: merged, loading: false, error: null });
    })();
    return () => {
      cancelled = true;
    };
  }, [user, bypass, version, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}
